import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Check, FileText, Minus, Plus, ShoppingCart, Upload, X } from 'lucide-react'
import { groupList, missingRequired, requiredKeys, specAddonTotal, useOptionGroups } from '@/lib/options'
import { incompleteRows, syncPersonalizations } from '@/lib/personalization'
import { api } from '@/lib/api'
import { ARTWORK_ACCEPT, ARTWORK_HINT, MAX_ARTWORKS, artworkExt, type Artwork } from '@/lib/artwork'
import { formatDate } from '@/lib/date'
import { useDeliveryQuote } from '@/lib/delivery'
import { money } from '@/lib/money'
import { describeUrgentTiers } from '@/lib/urgent'
import { tierIcon, tierTint } from '@/lib/tiers'
import type { ItemSpec, PersonalizationRow, StorefrontProduct } from '@/types'
import SpecFields from '@/components/order/SpecFields'
import { ArtworkThumb } from '@/components/order/ArtworkGallery'
import ArtworkPicker from '@/components/order/ArtworkPicker'
import Cta from '@/components/storefront/Cta'
import ProductGallery from '@/components/storefront/ProductGallery'
import LogoLoader from '@/components/ui/LogoLoader'
import { useToast } from '@/components/ui/toast'
import { useAuth } from '@/context/AuthContext'
import { useCart, cartItemFrom, groupBasePrice, groupSizesLabel, linePriceRows, lineSubtotal } from '@/context/CartContext'
import { cn } from '@/lib/cn'

export default function ProductDetail() {
  const { id } = useParams()
  const { user } = useAuth()
  const cart = useCart()
  const navigate = useNavigate()
  const toast = useToast()
  const optionGroups = useOptionGroups()

  const [spec, setSpec] = useState<ItemSpec>({})
  const [sizeMatrix, setSizeMatrix] = useState<Record<string, number>>({})
  const [wantsNames, setWantsNames] = useState(false)
  const [personalizations, setPersonalizations] = useState<PersonalizationRow[]>([])
  // A signed-in customer's uploaded files, up to MAX_ARTWORKS like checkout.
  const [artworks, setArtworks] = useState<Artwork[]>([])
  // The font behind the names (client request 2026-10-14). Checkout insists
  // on it for a line with names; here it is only collected early.
  const [fonts, setFonts] = useState<Artwork[]>([])
  // A guest cannot upload (the endpoint needs a sign-in), so only the picked
  // file's name is kept and checkout asks for the file.
  const [artworkName, setArtworkName] = useState<string | null>(null)
  // The guest's picked image straight from the browser, so it still shows as
  // a thumbnail. Images only: the site's CSP keeps pdf.js off blob: URLs.
  const [localImage, setLocalImage] = useState<string | null>(null)
  useEffect(
    () => () => {
      if (localImage) URL.revokeObjectURL(localImage)
    },
    [localImage],
  )

  const { data: product, isLoading } = useQuery({
    queryKey: ['storefront-product', id],
    queryFn: async () => (await api.get<{ data: StorefrontProduct }>(`/storefront/products/${id}`)).data.data,
  })

  // The same quote checkout's delivery calendar draws from, for one piece:
  // the earliest standard delivery, and the earliest urgent one when this
  // product can be rushed. Orders are placed by delivery date (2026-10-06).
  const estimate = useDeliveryQuote(id ? [{ productId: Number(id), pieces: 1 }] : [])

  if (isLoading || !product) {
    return (
      <div className="mx-auto w-full max-w-6xl px-5 py-10">
        <LogoLoader />
      </div>
    )
  }

  // Rows arrive sorted by tier rank, so the first row is the base (most expensive) tier.
  const rows = product.tier_prices ?? []
  const base = rows[0]
  const isCustomer = user?.role === 'customer'
  const viewerRow = isCustomer ? (rows.find((r) => r.tier_id === user?.tier?.id) ?? base) : undefined
  const viewerThreshold = viewerRow?.topup_threshold ?? -1
  const viewerTier = viewerRow?.tier ?? base?.tier ?? 'member'
  // A price agreed with this customer beats every tier row, and list_price is
  // the tier price it replaced, shown struck through beside it.
  const agreed = product.special_price ?? null
  const listPrice = agreed === null ? null : (product.list_price ?? null)
  // Header price and tier cards describe the product; the summary and total
  // reflect the current configuration, add-ons included.
  const perPiece = agreed ?? viewerRow?.price ?? base?.price ?? product.from_price
  const addonPerPc = specAddonTotal(product.options?.option_addons, spec, optionGroups.data)
  // The same choices at the customer's tier figures, where an add-on agreed
  // with them differs; struck through beside theirs like list_price.
  const tierAddonPerPc = specAddonTotal(
    {
      ...product.options?.option_addons,
      ...Object.fromEntries(
        Object.entries(product.list_option_addons ?? {}).map(([key, values]) => [
          key,
          { ...product.options?.option_addons?.[key], ...values },
        ]),
      ),
    },
    spec,
    optionGroups.data,
  )
  const namesPerPc = wantsNames ? (product.options?.personalization_price ?? 0) : 0
  const configuredPerPiece = perPiece + addonPerPc + namesPerPc
  const totalPcs = Object.values(sizeMatrix).reduce((a, b) => a + b, 0)
  // Priced the way the cart and checkout price it, so sizes in a size price
  // group (Kids 22-32) pay their own figure.
  const sizeGroups = product.size_price_groups ?? []
  const draftLine = {
    uid: '',
    ...cartItemFrom(product, { spec, sizeMatrix, personalizations: wantsNames ? personalizations : [], artworkName: null }),
  }
  const priceRows = linePriceRows(draftLine, viewerTier, optionGroups.data)
  const total = lineSubtotal(draftLine, viewerTier, optionGroups.data)
  const chosenSizes = product.sizes.filter((size) => (sizeMatrix[size] ?? 0) > 0)

  const setQty = (size: string, qty: number) => {
    const next = { ...sizeMatrix, [size]: Math.max(0, qty) }
    setSizeMatrix(next)
    if (wantsNames) setPersonalizations((rows) => syncPersonalizations(rows, next, product.sizes))
  }

  const toggleNames = (on: boolean) => {
    setWantsNames(on)
    setPersonalizations(on ? syncPersonalizations(personalizations, sizeMatrix, product.sizes) : [])
  }

  /**
   * A guest's file is name-captured only; checkout warns until it is
   * attached. A signed-in customer uploads through ArtworkPicker instead, so
   * the order carries the real files - a name-only order prints a job sheet
   * with nothing behind it.
   */
  const pickGuestArtwork = (file: File | null | undefined) => {
    if (!file) return
    setArtworkName(file.name)
    setLocalImage(file.type.startsWith('image/') ? URL.createObjectURL(file) : null)
  }

  const clearGuestArtwork = () => {
    setArtworkName(null)
    setLocalImage(null)
  }

  const addToCart = (): boolean => {
    if (totalPcs <= 0) {
      toast.error('Nothing to add', 'Set at least one piece in the size quantities first.')
      return false
    }
    if (wantsNames && incompleteRows(personalizations).length > 0) {
      toast.error('Names incomplete', 'Fill in a name and number for every piece, or set Name & number to No.')
      return false
    }
    const missing = missingRequired(spec, requiredKeys(product.options), optionGroups.data)
    if (missing.length > 0) {
      toast.error('Choose your options', `Pick a ${groupList(missing)} first.`)
      return false
    }
    // artworkName is the first file's, which the cart and checkout show as
    // the line's artwork and warn about while no file is attached.
    cart.add(
      cartItemFrom(product, {
        spec,
        sizeMatrix,
        personalizations,
        artworkName: artworks[0]?.artwork_name ?? artworkName,
        artworks,
        fonts: wantsNames ? fonts : [],
      }),
    )
    toast.success('Added to cart', `${product.name} - ${totalPcs} pcs`)
    setSizeMatrix({})
    setWantsNames(false)
    setPersonalizations([])
    setArtworks([])
    setFonts([])
    clearGuestArtwork()
    return true
  }

  const checkOut = () => {
    if (!addToCart()) return
    if (!user) navigate('/login', { state: { from: '/checkout' } })
    else navigate('/checkout')
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-5 py-6 lg:py-8">
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Left: sticky product image (sticky lives on the wrapper - .box overrides position) */}
        <div className="w-full lg:w-[400px] lg:shrink-0">
          <div className="lg:sticky lg:top-[86px]">
            <nav className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground/50">
              <Link to="/products" className="transition hover:text-primary">
                Products
              </Link>
              <span className="text-foreground/30">&rsaquo;</span>
              <span className="font-bold text-foreground">{product.name}</span>
            </nav>
            <ProductGallery key={product.id} product={product} />
            <div className="mt-4">
              <h1 className="text-2xl font-extrabold uppercase leading-tight tracking-[-0.02em]">{product.name}</h1>
              <p className="mt-1 text-sm text-foreground/60">
                <span className="text-xl font-extrabold text-primary">{money(perPiece)}</span>
                {agreed !== null ? (
                  <>
                    {listPrice !== null && listPrice > agreed && (
                      <span className="ml-1.5 text-foreground/40 line-through">{money(listPrice)}</span>
                    )}{' '}
                    per piece, your agreed price
                  </>
                ) : (
                  <> per piece at {viewerTier} pricing</>
                )}
              </p>
              {sizeGroups.map((group) => (
                <p key={group.name} className="mt-0.5 text-sm text-foreground/60">
                  <span className="font-extrabold text-primary">{money(groupBasePrice(group, viewerTier))}</span> per piece for{' '}
                  {group.name} ({groupSizesLabel(group.sizes)})
                </p>
              ))}
              {!user && <p className="mt-1 text-xs text-danger">Sign in to enjoy member pricing.</p>}
            </div>
          </div>
        </div>

        {/* Right: configurator card, Waly-style sectioned form */}
        <div className="min-w-0 flex-1">
          <div className="box p-5 lg:p-6">
            <h2 className="text-2xl font-extrabold uppercase leading-tight tracking-[-0.02em] sm:text-3xl">Configure Your Print</h2>

            {/* Size quantities */}
            <section className="mt-5 border-b border-foreground/10 pb-5">
              <p className="text-base font-bold">
                Size quantities
                <span className="ml-1 text-danger" aria-hidden="true">
                  *
                </span>
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4">
                {product.sizes.map((size) => {
                  const qty = sizeMatrix[size] ?? 0
                  return (
                    <div key={size}>
                      <label className="mb-1 block text-2xs font-bold uppercase tracking-wider text-foreground/50">
                        {size}
                      </label>
                      <div className="flex items-center overflow-hidden rounded-lg border border-foreground/20 transition-colors focus-within:border-primary">
                        <button
                          type="button"
                          aria-label={`Decrease ${size}`}
                          onClick={() => setQty(size, qty - 1)}
                          className="flex h-9 w-9 shrink-0 items-center justify-center bg-foreground/5 text-foreground/70 transition-colors hover:bg-foreground/10 disabled:cursor-not-allowed disabled:opacity-40"
                          disabled={qty <= 0}
                        >
                          <Minus className="size-3.5" />
                        </button>
                        <input
                          type="number"
                          min={0}
                          inputMode="numeric"
                          value={qty === 0 ? '' : qty}
                          placeholder="0"
                          onChange={(e) => setQty(size, Number(e.target.value))}
                          className="h-9 w-full min-w-0 bg-background text-center text-sm font-bold text-foreground outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        />
                        <button
                          type="button"
                          aria-label={`Increase ${size}`}
                          onClick={() => setQty(size, qty + 1)}
                          className="flex h-9 w-9 shrink-0 items-center justify-center bg-foreground/5 text-foreground/70 transition-colors hover:bg-foreground/10"
                        >
                          <Plus className="size-3.5" />
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>

            {/* Garment options (collar / sleeve / material / personalization) */}
            <SpecFields
              product={product}
              spec={spec}
              onSpec={setSpec}
              personalizations={personalizations}
              onPersonalizations={setPersonalizations}
              personalizationOn={wantsNames}
              onPersonalizationToggle={toggleNames}
              onImport={({ sizeMatrix: imported, rows }) => {
                setSizeMatrix(imported)
                setPersonalizations(rows)
              }}
              showRemark={false}
            />

            {/* The font behind the names, for the floor's size layout. */}
            {wantsNames && (
              <section className="mb-5 border-b border-foreground/10 pb-5">
                <p className="text-base font-bold">
                  Font File <span className="text-sm font-normal text-danger">(Required for names)</span>
                </p>
                <p className="mt-0.5 text-xs text-foreground/50">
                  {isCustomer
                    ? 'The font used for the names and numbers, so we lay them out exactly as you designed.'
                    : 'The font used for the names and numbers. Sign in to upload it now, or attach it at checkout.'}
                </p>
                {isCustomer && <ArtworkPicker kind="font" variant="dropzone" preview={false} value={fonts} onChange={setFonts} />}
              </section>
            )}

            {/* Upload artwork */}
            <section className="border-b border-foreground/10 pb-5">
              <p className="text-base font-bold">Upload Artwork</p>
              <p className="mt-0.5 text-xs text-foreground/50">
                {isCustomer
                  ? 'Your files upload here and follow the order to production.'
                  : `Sign in to upload now, or attach up to ${MAX_ARTWORKS} files at checkout. The file name is kept for now.`}
              </p>
              {isCustomer ? (
                <ArtworkPicker variant="dropzone" preview={false} value={artworks} onChange={setArtworks} />
              ) : artworkName ? (
                <div className="mt-2 flex items-center gap-3 rounded-xl border border-foreground/10 bg-background p-2.5">
                  {localImage ? (
                    <ArtworkThumb file={{ name: artworkName, path: localImage, size: null, mime: null }} path={localImage} className="size-14" />
                  ) : (
                    <span className="flex size-14 shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg border border-foreground/10 bg-foreground/5 text-foreground/40">
                      <FileText className="size-5" strokeWidth={1.5} />
                      <span className="text-[10px] font-bold">{artworkExt({ name: artworkName })}</span>
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-foreground">{artworkName}</p>
                    <p className="text-2xs text-foreground/50">File not uploaded yet - attach it at checkout</p>
                  </div>
                  <button
                    type="button"
                    aria-label="Remove artwork file"
                    onClick={clearGuestArtwork}
                    className="-m-2 p-2 text-foreground/40 transition hover:text-danger"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              ) : (
                <label
                  className="mt-2 block cursor-pointer rounded-xl border-2 border-dashed border-foreground/20 bg-background p-5 text-center transition-colors hover:border-primary/50"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault()
                    pickGuestArtwork(e.dataTransfer.files?.[0])
                  }}
                >
                  <span className="flex items-center justify-center gap-2 text-sm text-foreground/50">
                    <Upload className="size-4" />
                    <span>
                      Drag &amp; drop or <span className="font-bold text-primary">browse files</span>
                    </span>
                  </span>
                  <span className="mt-1 block text-2xs text-foreground/40">{ARTWORK_HINT}</span>
                  <input
                    type="file"
                    className="hidden"
                    accept={ARTWORK_ACCEPT}
                    onChange={(e) => pickGuestArtwork(e.target.files?.[0])}
                  />
                </label>
              )}
            </section>

            {/* Remarks */}
            <section className="mt-5 border-b border-foreground/10 pb-5">
              <p className="text-base font-bold">
                Remarks <span className="text-sm font-normal text-foreground/40">(Optional)</span>
              </p>
              <textarea
                rows={2}
                placeholder="Special instructions..."
                value={spec.remark ?? ''}
                onChange={(e) => setSpec({ ...spec, remark: e.target.value || undefined })}
                className="mt-2.5 w-full resize-none rounded-xl border border-foreground/20 bg-background p-3 text-sm text-foreground outline-none transition-colors placeholder:text-foreground/40 focus:border-primary"
              />
            </section>

            {/* Item summary */}
            <div className="mt-5 flex flex-col gap-4 rounded-xl border border-foreground/10 p-4 sm:flex-row">
              <div className="min-w-0 flex-1">
                <p className="mb-2 text-2xs font-bold uppercase tracking-wider text-foreground/50">Item Summary</p>
                <div className="space-y-1.5 text-sm">
                  {priceRows.length > 1 ? (
                    priceRows.map((row) => (
                      <p key={row.group ?? ''} className="flex justify-between gap-3">
                        <span className="text-foreground/60">
                          {product.name} - {row.group ?? 'Standard'} ({row.qty} pcs)
                        </span>
                        <span className="font-semibold">{money(row.unit)}/pc</span>
                      </p>
                    ))
                  ) : (
                    <p className="flex justify-between gap-3">
                      <span className="text-foreground/60">
                        {product.name}
                        {priceRows[0]?.group && ` - ${priceRows[0].group}`}
                      </span>
                      <span className="font-semibold">{money(priceRows[0]?.unit ?? configuredPerPiece)}/pc</span>
                    </p>
                  )}
                  {(addonPerPc > 0 || tierAddonPerPc > addonPerPc) && (
                    <p className="flex justify-between gap-3 text-xs text-foreground/60">
                      <span>Includes option add-ons</span>
                      <span>
                        {tierAddonPerPc > addonPerPc && (
                          <span className="mr-1.5 text-foreground/40 line-through">+RM{tierAddonPerPc.toFixed(2)}</span>
                        )}
                        <span className="font-semibold text-foreground">+RM{addonPerPc.toFixed(2)}/pc</span>
                      </span>
                    </p>
                  )}
                  {namesPerPc > 0 && (
                    <p className="flex justify-between gap-3 text-xs text-foreground/60">
                      <span>Name &amp; number</span>
                      <span className="font-semibold text-foreground">+RM{namesPerPc.toFixed(2)}/pc</span>
                    </p>
                  )}
                  <p className="flex justify-between gap-3">
                    <span className="text-foreground/60">Pieces</span>
                    <span className="font-semibold">
                      {totalPcs > 0
                        ? `${chosenSizes.map((size) => `${size} x ${sizeMatrix[size]}`).join(', ')} = ${totalPcs} pcs`
                        : '0 pcs'}
                    </span>
                  </p>
                  <p className="flex justify-between gap-3 border-b border-foreground/5 pb-2">
                    <span className="text-foreground/60">Delivery fees</span>
                    <span className="text-foreground/60">Calculated at checkout</span>
                  </p>
                  {estimate.data && (
                    <>
                      <p className="flex justify-between gap-3 pt-1 text-xs text-foreground/60">
                        <span>Earliest delivery</span>
                        <span className="font-semibold text-foreground">{formatDate(estimate.data.standard_date)}</span>
                      </p>
                      {estimate.data.urgent_date && estimate.data.urgent_date < estimate.data.standard_date && (
                        <p className="flex justify-between gap-3 text-xs text-foreground/60">
                          <span>With urgent service</span>
                          <span className="font-semibold text-foreground">{formatDate(estimate.data.urgent_date)}</span>
                        </p>
                      )}
                    </>
                  )}
                  {/* Advertised here; urgency follows from the delivery date
                      picked at checkout, which applies to the whole order. */}
                  {(product.urgent_tiers ?? []).some((t) => t.price > 0) && (
                    <p className="flex justify-between gap-3 text-xs text-foreground/60">
                      <span className="shrink-0">Need it faster?</span>
                      <span className="text-right font-semibold text-foreground">
                        Urgent {describeUrgentTiers(product.urgent_tiers!)} - pick an earlier delivery date at checkout
                      </span>
                    </p>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 flex-col justify-between border-foreground/10 sm:w-[240px] sm:border-l sm:pl-4">
                <div className="mb-3">
                  <p className="mb-1 text-2xs font-bold uppercase tracking-wider text-foreground/50">Total</p>
                  <p className="text-3xl font-extrabold leading-none tracking-tight text-primary">
                    {totalPcs > 0 ? money(total) : '--'}
                  </p>
                </div>
                <div>
                  <Cta onClick={() => addToCart()} fullWidth size="md">
                    <ShoppingCart className="size-4" /> Add to cart
                  </Cta>
                  <Cta onClick={checkOut} variant="white" fullWidth size="md" arrow className="mt-2">
                    Check out
                  </Cta>
                  {cart.items.length > 0 && (
                    <p className="mt-2 text-center text-xs text-foreground/50">
                      {cart.items.length} item(s) in your{' '}
                      <Link to="/cart" className="font-semibold text-primary hover:underline">
                        cart
                      </Link>
                    </p>
                  )}
                  {!user && (
                    <p className="mt-2 text-center text-2xs text-foreground/50">
                      <Link to="/login" className="font-semibold text-primary hover:underline">
                        Sign in
                      </Link>{' '}
                      to enjoy member pricing.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Member pricing tiers */}
      {rows.length > 0 && base && (
        <div className="box mt-6 p-5">
          {(() => {
            const cheapest = Math.min(...rows.map((r) => r.price))
            const maxSaving = base.price - cheapest
            const maxPct = base.price > 0 ? Math.round((maxSaving / base.price) * 100) : 0
            const topRow = rows[rows.length - 1]
            const atTopTier = Boolean(isCustomer && viewerRow && topRow && viewerRow.tier_id === topRow.tier_id)
            const ViewerIcon = tierIcon(viewerRow?.icon)
            // An agreed price is not a rung on this ladder, so neither the
            // top-up nudge nor the top-tier congratulation is true for them.
            if (agreed !== null) {
              return (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5">
                  <p className="text-sm font-bold uppercase text-primary">
                    You pay {money(agreed)} per piece, agreed with MPG
                  </p>
                  {listPrice !== null && listPrice > agreed && (
                    <span className="shrink-0 rounded-full bg-primary px-2.5 py-0.5 text-xs font-bold text-white">
                      Save {money(listPrice - agreed)}/pc
                    </span>
                  )}
                </div>
              )
            }
            return atTopTier && viewerRow ? (
              <div className="flex items-center justify-between rounded-lg border px-4 py-2.5" style={tierTint(viewerRow.color)}>
                <p className="text-sm font-bold uppercase">You're on {viewerRow.tier} - our best member prices</p>
                <ViewerIcon className="size-4" />
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3 rounded-lg bg-primary bg-noise px-4 py-2.5 text-white">
                <p className="text-sm font-bold uppercase">Hurry! Top up now to get more discount!</p>
                {maxPct > 0 && (
                  <span className="shrink-0 rounded-full bg-white/90 px-2.5 py-0.5 text-xs font-bold text-primary">
                    Up to {maxPct}% off
                  </span>
                )}
              </div>
            )
          })()}
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {rows.map((row) => {
              const Icon = tierIcon(row.icon)
              const tint = tierTint(row.color)
              const isViewer = agreed === null && isCustomer && viewerRow ? row.tier_id === viewerRow.tier_id : false
              const unlocked = isCustomer && !isViewer && row.topup_threshold <= viewerThreshold
              const saving = base.price - row.price
              const pct = base.price > 0 && saving > 0 ? Math.round((saving / base.price) * 100) : 0
              return (
                <div
                  key={row.tier_id ?? row.tier}
                  className={cn('rounded-xl border p-4 text-center', unlocked && 'opacity-70')}
                  style={
                    isViewer
                      ? { borderColor: tint.color, backgroundColor: tint.backgroundColor, boxShadow: `0 0 0 1px ${tint.color}` }
                      : { borderColor: tint.borderColor }
                  }
                >
                  <span className="mx-auto flex size-9 items-center justify-center rounded-full border" style={tint}>
                    <Icon className="size-4" />
                  </span>
                  <p className="mt-2 font-bold" style={{ color: tint.color }}>
                    {row.tier}
                  </p>
                  <p className="text-2xs uppercase text-foreground/50">
                    {row.topup_threshold > 0 ? `Top up ${money(row.topup_threshold)}+` : 'No top-up needed'}
                  </p>
                  <p className="mt-2 text-2xl font-extrabold text-primary">{money(row.price)}</p>
                  <p className="text-2xs uppercase text-foreground/50">per piece</p>
                  {sizeGroups.map((group) => (
                    <p key={group.name} className="mt-1 text-xs text-foreground/60">
                      {group.name}: <span className="font-bold text-foreground">{money(groupBasePrice({ ...group, special_price: null }, row.tier))}</span>
                    </p>
                  ))}
                  {pct > 0 ? (
                    <p className="mt-1 text-xs font-semibold text-success">
                      -{pct}% · Save {money(saving)}/pc
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-foreground/40">Base price</p>
                  )}
                  {isViewer ? (
                    <p className="mt-3 text-xs font-bold uppercase" style={{ color: tint.color }}>
                      Your tier
                    </p>
                  ) : unlocked ? (
                    <p className="mt-3 inline-flex items-center justify-center gap-1 text-xs font-bold uppercase text-success">
                      <Check className="size-3.5" /> Unlocked
                    </p>
                  ) : (
                    <Cta
                      fullWidth
                      arrow
                      size="md"
                      className="mt-3"
                      onClick={() => navigate(isCustomer ? '/account/wallet/topup' : '/register')}
                    >
                      {isCustomer ? 'Top Up Now' : 'Sign up & top up'}
                    </Cta>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
